"use client";
import { PieChart, Pie, ResponsiveContainer, Cell } from "recharts";
import StatusBadge from "../primitives/status-badge";
import { formatDiff, formatPercentage } from "@/utils/numbers";
import React from "react";
import { BettorMetricsTimeframeContext, BettorRecordBasic } from "common";

interface CustomLabelProps {
  cx: number;
  cy: number;
  midAngle: number;
  innerRadius: number;
  outerRadius: number;
  name: string;
  percent?: number;
  value: number;
  winRate: number;
}
const renderCustomLabel = (props: CustomLabelProps) => {
  const labelDistance = 24;
  let x, y;
  y = props.cy;
  if (props.name === "Win") {
    x = props.cx - props.outerRadius - labelDistance;
  } else if (props.name === "Lose") {
    x = props.cx + props.outerRadius + labelDistance;
  } else {
    return null;
  }
  const iconX = props.cx - 16 / 2;
  return (
    <>
      <text
        x={x}
        y={y}
        fill="black"
        textAnchor={props.name === "Win" ? "end" : "start"}
        dominantBaseline="central"
      >
        <tspan
          x={x}
          // dy="-0.5rem"
          y={y - 8}
          textAnchor="middle"
          className="text-title-lg-medium"
        >
          {props.value.toLocaleString()}
        </tspan>
        <tspan
          x={x}
          // dy="1rem"
          y={y + 16}

          textAnchor="middle"
          className="text-label-sm-medium uppercase"
        >
          {props.name}
        </tspan>
      </text>
      {props.winRate > 50 ? (
        <svg
          x={iconX}
          y={"53.5%"}
          xmlns="http://www.w3.org/2000/svg"
          width="16"
          height="16"
          viewBox="0 0 16 16"
          fill="none"
        >
          <path
            d="M10.6614 4L12.1881 5.52668L8.93477 8.78005L6.26811 6.11335L1.32812 11.0601L2.26812 12.0001L6.26811 8.00004L8.93477 10.6667L13.1348 6.47336L14.6614 8.00004V4H10.6614Z"
            fill="var(--primary-300)"
          />
        </svg>
      ) : (
        props.winRate < 50 && (
          <svg
            x={iconX}
            y={"53.5%"}
            xmlns="http://www.w3.org/2000/svg"
            width="16"
            height="16"
            viewBox="0 0 16 16"
            fill="none"
          >
            <path
              d="M10.6614 12L12.1881 10.4733L8.93477 7.21999L6.26811 9.88665L1.32812 4.94L2.26812 4L6.26811 7.99999L8.93477 5.33333L13.1348 9.52665L14.6614 7.99999V12H10.6614Z"
              fill="var(--error-400)"
            />
          </svg>
        )
      )}
    </>
  );
};
interface PerformanceChartProps {
  label: string;
  data: BettorRecordBasic;
}

function PerformanceChart(props: PerformanceChartProps) {
  const data = [
    { name: "Win", value: props.data.wins, color: "var(--primary-600)" },
    { name: "Lose", value: props.data.losses, color: "var(--secondary-300)" },
    { name: "Tie", value: props.data.ties, color: "var(--secondary-600)" },
  ];
  const total = props.data.wins + props.data.losses + props.data.ties;
  
  if (props.data.wins === undefined || props.data.losses === undefined) {
    return <div>Loading...</div>;
  } else {
    return (
      <>
        <div className="flex items-center gap-2">
          <div className="text-label-sm-medium uppercase text-secondary-500">
            {props.label}
          </div>
          <StatusBadge
            label={`${formatDiff(props.data.unitsGained ? props.data.unitsGained : 0)} Units`}
            status={`${
              props.data.unitsGained > 0
                ? "success"
                : props.data.unitsGained < 0
                ? "error"
                : "default"
            }`}
          />
        </div>
        <div className="flex items-center w-full h-[96px]">
          {total !== 0 ? (
            <ResponsiveContainer width="100%" height={64}>
              <PieChart height={64} width={64}>
                <Pie
                  animationDuration={150}
                  animationEasing="ease-in-out"
                  data={data}
                  dataKey="value"
                  cx="50%"
                  cy="50%"
                  innerRadius={20}
                  outerRadius={32}
                  textAnchor="middle"
                  dominantBaseline={"central"}
                  paddingAngle={2}
                  label={(labelProps) =>
                    renderCustomLabel({
                      winRate: props.data.winRate,
                      ...labelProps,
                    })
                  }
                  labelLine={false}
                  startAngle={270}
                  endAngle={-90}
                >
                  {data.map((entry, index) => (
                    <Cell key={`cell-${entry.name}-${index}`} fill={entry.color} />
                  ))}
                </Pie>

                <text
                  className="text-title-sm-medium"
                  x={"50%"}
                  y={`${props.data.winRate !== 0 ? "46%" : "50%"}`}
                  fill={`${
                    props.data.winRate > 50
                      ? "var(--primary-300)"
                      : "var(--error-400)"
                  }`}
                  textAnchor="middle"
                  dominantBaseline="central"
                >
                  {props.data.winRate.toFixed(0)}%
                </text>
              </PieChart>
            </ResponsiveContainer>
          ) : (
            <div className="text-label-bold text-secondary uppercase w-full text-center">
              No Activity
            </div>
          )}
        </div>
      </>
    );
  }
}

export default PerformanceChart;
